import { useEffect, useRef } from "react";
import { useCanvasInteractionEffects } from "@/hooks/use-canvas-interaction-effects";
import type { PageIndicator } from "@shared/schema";

interface UseMainViewingAreaEffectsProps {
  // Low rating indicator exposure
  onLowRatingIndicator?: (handler: (rating: number, position: { x: number; y: number }) => void) => void;
  handleLowRatingIndicator: (rating: number, position: { x: number; y: number }) => void;
  
  // Canvas interaction effects props
  pageIndicators: PageIndicator[];
  redraw: () => void;
  activeIndicator: PageIndicator | null;
  pendingIndicator: {
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
    number: number;
  } | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  getCurrentCursor: () => string;
  setCurrentCursor: (cursor: string) => void;
  handlePointerClick: () => { x: number; y: number } | null;
  getIndicatorAtPosition: (x: number, y: number) => PageIndicator | null;
  imageToCanvasCoords: (x: number, y: number) => { x: number; y: number };
  canvasToImageCoords: (x: number, y: number) => { x: number; y: number };
  setActiveIndicator: (indicator: PageIndicator | null) => void;
  setPopoverPosition: (position: { x: number; y: number } | null) => void;
  viewMode: "comment" | "browse";
  dragJustCompleted: boolean;
  setDragJustCompleted: (completed: boolean) => void;
  isDraggingIndicator: () => boolean;
  setPendingIndicator: (indicator: any) => void;
  onIndicatorChange: (indicatorNumber: number | null) => void;
  
  // Version restriction props
  currentVersion?: number;
  latestVersion?: number;
  onVersionRestricted?: () => void;
  
  // Image loading props
  currentPageData?: { webpUrl: string | null; imageUrl: string | null; width: number; height: number } | null;
  drawImage: (url: string, width: number, height: number) => Promise<void>;
}

export function useMainViewingAreaEffects({
  onLowRatingIndicator,
  handleLowRatingIndicator,
  pageIndicators,
  redraw,
  activeIndicator,
  pendingIndicator,
  textareaRef,
  getCurrentCursor,
  setCurrentCursor,
  handlePointerClick,
  getIndicatorAtPosition,
  imageToCanvasCoords,
  canvasToImageCoords,
  setActiveIndicator,
  setPopoverPosition,
  viewMode,
  dragJustCompleted,
  setDragJustCompleted,
  isDraggingIndicator,
  setPendingIndicator,
  onIndicatorChange,
  currentVersion,
  latestVersion,
  onVersionRestricted,
  currentPageData,
  drawImage,
}: UseMainViewingAreaEffectsProps) {
  
  // Expose handleLowRatingIndicator to parent component
  useEffect(() => {
    if (onLowRatingIndicator) {
      onLowRatingIndicator(handleLowRatingIndicator);
    }
  }, [handleLowRatingIndicator, onLowRatingIndicator]);

  // Canvas interaction effects (extracted into custom hook)
  useCanvasInteractionEffects({
    pageIndicators,
    redraw,
    activeIndicator,
    pendingIndicator,
    textareaRef,
    getCurrentCursor,
    setCurrentCursor,
    handlePointerClick,
    getIndicatorAtPosition,
    imageToCanvasCoords,
    canvasToImageCoords,
    setActiveIndicator,
    setPopoverPosition,
    viewMode,
    dragJustCompleted,
    setDragJustCompleted,
    isDraggingIndicator,
    setPendingIndicator,
    onIndicatorChange,
    currentVersion,
    latestVersion,
    onVersionRestricted,
  });

  // Prevent document-level zoom gestures
  // COMMENTED OUT: This was causing touch sensitivity issues across the entire app
  // useEffect(() => {
  //   const preventZoom = (e: TouchEvent) => {
  //     // If there are multiple touches (pinch gesture), prevent default
  //     if (e.touches.length > 1) {
  //       e.preventDefault();
  //     }
  //   };

  //   const preventDoubleTapZoom = (e: TouchEvent) => {
  //     e.preventDefault();
  //   };

  //   // Add event listeners to document to catch any zoom attempts
  //   window.document.addEventListener('touchstart', preventZoom, { passive: false });
  //   window.document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
  //   window.document.addEventListener('touchmove', preventZoom, { passive: false });

  //   return () => {
  //     window.document.removeEventListener('touchstart', preventZoom);
  //     window.document.removeEventListener('touchend', preventDoubleTapZoom);
  //     window.document.removeEventListener('touchmove', preventZoom);
  //   };
  // }, []);

  // REMOVED: Old addIndicatorMutation - now using ironclad validation via addCommentMutation only

  // Load image when page changes
  useEffect(() => {
    if (currentPageData?.webpUrl) {
      drawImage(currentPageData.webpUrl, currentPageData.width, currentPageData.height)
        .catch(error => {
          console.error('Failed to load image:', error);
        });
    }
  }, [currentPageData, drawImage]);
}